import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AiAssistant from './AiAssistant';
import './AiAssistantFab.css';

const STORAGE_KEY = 'atozasAiFabPos';
const SIZE = 58;
const MARGIN = 16;
const DRAG_THRESHOLD = 6;

const defaultPos = () => ({
  x: Math.max(MARGIN, window.innerWidth - SIZE - MARGIN),
  y: Math.max(MARGIN, window.innerHeight - SIZE - MARGIN - 8),
});

const clampPos = (x, y) => {
  const maxX = Math.max(MARGIN, window.innerWidth - SIZE - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - SIZE - MARGIN);
  return {
    x: Math.min(maxX, Math.max(MARGIN, x)),
    y: Math.min(maxY, Math.max(MARGIN, y)),
  };
};

const loadPos = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPos();
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return defaultPos();
    return clampPos(parsed.x, parsed.y);
  } catch (_) {
    return defaultPos();
  }
};

/**
 * Draggable FAB that toggles the floating AI chatbot panel.
 */
const AiAssistantFab = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const btnRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);

  const [pos, setPos] = useState(() =>
    typeof window !== 'undefined' ? loadPos() : { x: 0, y: 0 }
  );
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setPos((prev) => clampPos(prev.x, prev.y));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Allow other UI (e.g. deep links) to open the chatbot.
  useEffect(() => {
    const openChat = () => setOpen(true);
    window.addEventListener('openAiAssistant', openChat);
    return () => window.removeEventListener('openAiAssistant', openChat);
  }, []);

  // /ai-assistant route redirects here with state — open the widget.
  useEffect(() => {
    if (location.state?.openAiAssistant) {
      setOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      /* ignore */
    }
  }, []);

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pos.x,
      originY: pos.y,
      moved: false,
      pointerId: e.pointerId,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch (_) {
      /* older browsers */
    }
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      setDragging(true);
    }

    setPos(clampPos(drag.originX + dx, drag.originY + dy));
  };

  const endDrag = (e) => {
    const drag = dragRef.current;
    if (!drag || (e.pointerId != null && drag.pointerId !== e.pointerId)) return;

    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {
      /* ignore */
    }

    if (drag.moved) {
      suppressClickRef.current = true;
      setPos((prev) => {
        const next = clampPos(prev.x, prev.y);
        persist(next);
        return next;
      });
    }
    setDragging(false);
    dragRef.current = null;
  };

  const onClick = (e) => {
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
      return;
    }
    setOpen((prev) => !prev);
  };

  const isLeft = typeof window !== 'undefined' && pos.x < window.innerWidth / 2;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`aia-fab ${dragging ? 'is-dragging' : ''} ${isLeft ? 'is-left' : ''} ${open ? 'is-open' : ''}`}
        style={{ left: pos.x, top: pos.y }}
        aria-label={open ? 'Close AI Assistant' : 'Open AI Assistant'}
        aria-expanded={open}
        title="AI Assistant — drag to move"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={onClick}
      >
        <svg className="aia-fab-icon" viewBox="0 0 64 64" aria-hidden="true">
          <defs>
            <linearGradient id="aiaFabGrad" x1="8" y1="56" x2="56" y2="8" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#2F6BFF" />
              <stop offset="55%" stopColor="#7B3FF2" />
              <stop offset="100%" stopColor="#C026D3" />
            </linearGradient>
          </defs>
          <circle
            cx="32"
            cy="32"
            r="24"
            fill="none"
            stroke="url(#aiaFabGrad)"
            strokeWidth="3.2"
            strokeLinecap="round"
            strokeDasharray="118 34"
            strokeDashoffset="28"
            transform="rotate(-25 32 32)"
          />
          <path
            fill="url(#aiaFabGrad)"
            d="M32 18.5c-7.6 0-13.8 5.4-13.8 12.1 0 4.2 2.4 7.9 6.2 10.1v5.2l5.4-3.1c.7.1 1.4.2 2.2.2 7.6 0 13.8-5.4 13.8-12.1S39.6 18.5 32 18.5z"
          />
          <circle cx="25.2" cy="30.6" r="1.85" fill="#fff" />
          <circle cx="32" cy="30.6" r="1.85" fill="#fff" />
          <circle cx="38.8" cy="30.6" r="1.85" fill="#fff" />
          <path
            fill="url(#aiaFabGrad)"
            d="M48.2 12.2l1.55 4.35 4.35 1.55-4.35 1.55-1.55 4.35-1.55-4.35-4.35-1.55 4.35-1.55z"
          />
        </svg>
        <span className="aia-fab-tooltip">{open ? 'Close chat' : 'AI Assistant'}</span>
      </button>

      <AiAssistant
        currentUser={currentUser}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default AiAssistantFab;
